import React, {useCallback, useRef} from 'react';

import {toast} from 'react-toastify';

import {validateCodeWithHsn} from 'api';
import {
    $invoiceLinesHolder,
    $invoiceLinesHolderCodesTotal,
    setInvoiceLineChargeData,
    setInvoiceLinesHolder,
    updateInvoiceLinesHolder,
} from 'models/documents/invoices/create';
import {$currentScannedGtin, setCurrentScannedGtin} from 'models/documents/invoices/details';
import {CUSTOMER_PACKAGE_TYPES, PackageTypes, TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {InvocieLineHolder, InvoiceLine} from 'types/invoices';
import {CodeResponseType, CodeResponseTypeInfo} from 'types/validation';
import {invoiceValidationWithTransportCodes} from 'utils/markingCodes';

interface UseCodeScannerProps {
    combineByGtin: boolean;
    handleEnterFromLine: (response: CodeResponseType['info']) => boolean;
    clearCodeAndDescription: () => void;
    setScanPending: (pending: boolean) => void;
}

interface CodeScannerReturn {
    onEnterCode: (code: string, fromLine?: boolean) => Promise<void>;
    codeGtinHolder: React.MutableRefObject<string>;
}

const DEFAULT_INVOICE_LINE_BASE = {
    qty: '1',
    isMarkedItem: true,
    price: '',
    lines: '',
    vatCategory: {label: '', value: ''},
    vat: '',
    lineAmountWVat: '',
    isHsn: 'HS' as const,
};

const setCopyActions = (packagingType: PackageTypes, allowToDelete: boolean) => {
    const ALLOWED_TO_PASTE_TYPES = [PackageTypes.UNIT, PackageTypes.GROUP];
    return {
        allowToCopy: false,
        allowToDuplicateDown: false,
        allowToPaste: ALLOWED_TO_PASTE_TYPES.includes(packagingType),
        allowToDelete,
    };
};

function handleTransportCodes(
    codeInfo: CodeResponseType['info'],
    includeCode = true,
    isCombined = false,
) {
    const {
        packagingType,
        identityCode,
        gtin,
        childrenMarkingCodes,
        productName,
        hsnDescription,
        hsnCode,
    } = codeInfo;
    // if (TRANSPORT_PACKAGE_TYPES.includes(packagingType)) {
    //     return {
    //         isMarkedItem: true,
    //         code: identityCode,
    //         gtin,
    //         description: packagingType,
    //         childrenMarkingCodes,
    //         packagingType,
    //         copyActions: setCopyActions(packagingType, true),
    //     };
    // }
    return {
        ...DEFAULT_INVOICE_LINE_BASE,
        description: productName,
        serviceCategory: {
            label: hsnDescription ?? hsnCode,
            value: hsnCode,
        },
        gtin: TRANSPORT_PACKAGE_TYPES.includes(packagingType) ? identityCode : gtin,
        code: includeCode ? identityCode : '',
        packagingType,
        childrenMarkingCodes,
        isCombined,
        copyActions: setCopyActions(packagingType, true),
        ...(TRANSPORT_PACKAGE_TYPES.includes(packagingType) && {
            price: '0',
            lines: '0',
            lineAmountWVat: '0',
            vat: '0',
            vatCategory: {label: 'Z - option', value: 'Z'},
        }),
    };
}

// function createInvoiceLineFromCode(
//     codeInfo: CodeResponseType['info'],
//     includeCode = true,
// ): Partial<InvoiceLine> {
//     return handleTransportCodes(codeInfo, includeCode);
// }

function shouldSkipValidation(
    code: string,
    currentCode: string,
): 'duplicate' | 'short' | 'empty' | null {
    if (code.length === 0) {
        return 'empty';
    }
    if (currentCode === code) {
        return 'duplicate';
    }
    if (code.length < 10) {
        return 'short';
    }
    return null;
}

function showValidationErrors(errors: string[]): void {
    toast.error(
        <div>
            <strong>Errors</strong>
            <ul style={{margin: '6px 0'}}>
                {errors.map((error) => (
                    <li key={error}>{error}</li>
                ))}
            </ul>
        </div>,
    );
}

const shouldHaveChildrenMarkingCodes = (item: InvocieLineHolder | CodeResponseTypeInfo) => {
    const {childrenMarkingCodes, ...rest} = item;

    if (!childrenMarkingCodes?.length || item.packagingType === PackageTypes.GROUP) {
        return false;
    }
    return true;
};
function codeInfoToInvoiceLine(child: CodeResponseTypeInfo): InvocieLineHolder {
    const {childrenMarkingCodes, packagingType, ...base} = handleTransportCodes(child, true, false);
    const shouldHaveChildren = shouldHaveChildrenMarkingCodes(child);

    const hasExistingCodes = childrenMarkingCodes?.some((code) =>
        $invoiceLinesHolderCodesTotal.getState().includes(code.identityCode),
    );

    if (hasExistingCodes) {
        throw new Error('Code already exists');
    }

    return {
        ...DEFAULT_INVOICE_LINE_BASE,
        ...base,
        lines: '',
        lineAmountWVat: '',
        packagingType,
        copyActions: setCopyActions(child.packagingType, false),
        ...(shouldHaveChildren && {
            subRows: childrenMarkingCodes.map(codeInfoToInvoiceLine),
        }),
        ...(TRANSPORT_PACKAGE_TYPES.includes(packagingType) && {
            price: '0',
            lines: '0',
            lineAmountWVat: '0',
            vat: '0',
            vatCategory: {label: 'Z - option', value: 'Z'},
        }),
    } as InvocieLineHolder;
}

function mapChildrenMarkingCodes(item: InvocieLineHolder) {
    const {childrenMarkingCodes, ...rest} = item;

    // console.log('childrenMarkingCodes', childrenMarkingCodes);
    // console.log('invoiceLinesHolderCodesTotal', $invoiceLinesHolderCodesTotal.getState());
    // const hasExistingCodes = childrenMarkingCodes?.some((code) =>
    //     $invoiceLinesHolderCodesTotal.getState().includes(code.identityCode),
    // );

    // if (hasExistingCodes) {
    //     throw new Error('Code already exists');
    // }

    const shouldHaveChildren = shouldHaveChildrenMarkingCodes(item);

    if (!shouldHaveChildren) return rest;

    return {
        ...rest,
        subRows: childrenMarkingCodes?.map(codeInfoToInvoiceLine),
    };
}

export function combineSiblingUnitsByGtin(lines: InvocieLineHolder[]): InvocieLineHolder[] {
    const result: InvocieLineHolder[] = [];
    const byGtin = new Map<string, InvocieLineHolder[]>();

    for (const line of lines) {
        const {gtin, price, charges, vat, serviceCategory} = line;
        const {isCharge, amountXaf, reason} = charges ?? {};

        const hashKeyObject = JSON.stringify({
            gtin,
            price,
            charges: isCharge,
            rate: amountXaf,
            reason,
            vat,
            category: serviceCategory?.value,
        });
        const isUnitWithGtin =
            line.packagingType &&
            CUSTOMER_PACKAGE_TYPES.includes(line.packagingType) &&
            line.gtin != null &&
            line.gtin !== '';
        if (isUnitWithGtin) {
            const list = byGtin.get(hashKeyObject) ?? [];
            list.push(line);
            byGtin.set(hashKeyObject, list);
        } else {
            result.push(processLineSubRows(line));
        }
    }

    const toLowerAndTrim = (str: string) => str.toLowerCase().trim();

    Array.from(byGtin.values()).forEach((group) => {
        const first = group[0];
        const addCharges = first['charges']
            ? toLowerAndTrim(first['charges'].isCharge) === 'true'
                ? Number(first.charges.amountXaf)
                : -Number(first.charges.amountXaf)
            : 0;

        const qty = group.length;
        const lines = qty * Number(first['price']) + addCharges;
        const lineAmountWVat = (Number(first.vat) / 100) * lines + lines;
        const allCodes = group.flatMap((r: InvocieLineHolder) =>
            [r.code, ...(r.combinedCodes ?? [])].filter(Boolean),
        );
        const childSubRows = group.flatMap((r: InvocieLineHolder) => r.subRows ?? []);
        result.push({
            ...first,
            qty: String(qty),
            lines: lines.toFixed(2),
            lineAmountWVat: lineAmountWVat.toFixed(2),
            combinedCodes: allCodes.length ? allCodes : [first.code].filter(Boolean),
            subRows: childSubRows.length ? combineSiblingUnitsByGtin(childSubRows) : first.subRows,
        });
    });

    return result;
}

function processLineSubRows(line: InvocieLineHolder): InvocieLineHolder {
    if (!line.subRows?.length) return line;
    return {...line, subRows: combineSiblingUnitsByGtin(line.subRows)};
}

function handleTransportCodesCombineByGtin(codeInfo: CodeResponseType['info']) {
    const root = handleTransportCodes(codeInfo, true, true);
    const mapped = mapChildrenMarkingCodes(root) as InvocieLineHolder;
    const withSubRows = mapped.subRows?.length
        ? {...mapped, subRows: combineSiblingUnitsByGtin(mapped.subRows)}
        : mapped;
    setInvoiceLinesHolder([withSubRows]);
}

export function useCodeScanner({
    combineByGtin,
    handleEnterFromLine,
    clearCodeAndDescription,
    setScanPending,
}: UseCodeScannerProps): CodeScannerReturn {
    const codeGtinHolder = useRef<string>('');

    const handleCombineByGtinMode = useCallback(
        (codeInfo: CodeResponseType['info']) => {
            if (TRANSPORT_PACKAGE_TYPES.includes(codeInfo.packagingType)) {
                handleTransportCodesCombineByGtin(codeInfo);
                return;
            }

            const currentGtin = $currentScannedGtin.getState();

            if (currentGtin && codeInfo.gtin !== currentGtin) {
                toast.info(
                    'This code cannot be grouped with the current group because it has a different GTIN. It will be added as a separate line.',
                );
            }

            if (currentGtin === null || codeInfo.gtin !== currentGtin) {
                setCurrentScannedGtin(codeInfo.gtin);

                const newLine = {
                    ...handleTransportCodes(codeInfo, false, false),
                    combinedCodes: [codeInfo.identityCode],
                };

                setInvoiceLinesHolder([newLine]);
            } else {
                const currentLine = $invoiceLinesHolder.getState()[0];
                const newQty = Number(currentLine.qty) + 1;
                const updatedCodes = [...(currentLine.combinedCodes ?? []), codeInfo.identityCode];

                updateInvoiceLinesHolder({
                    depth: {step: 0, id: '0'},
                    data: [
                        {columnId: 'qty', value: newQty.toString()},
                        {columnId: 'combinedCodes', value: updatedCodes},
                    ],
                });
            }
        },
        [combineByGtin],
    );

    const handleNormalMode = useCallback((codeInfo: CodeResponseType['info']) => {
        const newLine = handleTransportCodes(codeInfo, true, false);
        const mappedLine = mapChildrenMarkingCodes(newLine) as InvocieLineHolder;

        setInvoiceLinesHolder([mappedLine]);
    }, []);

    const onEnterCode = useCallback(
        async (code: string, fromLine = false) => {
            if (fromLine) {
                const skipReason = shouldSkipValidation(code, codeGtinHolder.current);

                if (skipReason === 'duplicate') {
                    return;
                }

                if (skipReason === 'short' || skipReason === 'empty') {
                    clearCodeAndDescription();
                    codeGtinHolder.current = code;
                    return;
                }
            }

            try {
                setScanPending(true);

                const response = (await validateCodeWithHsn(code)).data;
                const validationErrors = invoiceValidationWithTransportCodes(
                    response,
                    $invoiceLinesHolderCodesTotal.getState(),
                );

                if (validationErrors.length > 0) {
                    showValidationErrors(validationErrors);

                    if (fromLine) {
                        clearCodeAndDescription();
                    }
                    return;
                }

                if (fromLine) {
                    let toClearLine = false;

                    const isTransport =
                        response.info.packagingType &&
                        !CUSTOMER_PACKAGE_TYPES.includes(response.info.packagingType);

                    if (isTransport) {
                        const newLine = handleTransportCodes(response.info, true, false);
                        const mappedLine = (mapChildrenMarkingCodes(
                            newLine,
                        ) as unknown) as CodeResponseType['info'];
                        toClearLine = handleEnterFromLine(mappedLine);
                    } else {
                        toClearLine = handleEnterFromLine(response.info);
                    }

                    if (toClearLine) {
                        clearCodeAndDescription();
                    }
                    return;
                }

                if (combineByGtin) {
                    handleCombineByGtinMode(response.info);
                } else {
                    handleNormalMode(response.info);
                }

                setInvoiceLineChargeData(null);
            } catch (error) {
                console.error(error);
                toast.error((error as Error).message ?? 'Invalid code');

                if (fromLine) {
                    clearCodeAndDescription();
                }
                codeGtinHolder.current = code;
            } finally {
                setScanPending(false);
            }
        },
        [
            combineByGtin,
            handleEnterFromLine,
            clearCodeAndDescription,
            setScanPending,
            handleCombineByGtinMode,
            handleNormalMode,
        ],
    );

    return {
        onEnterCode,
        codeGtinHolder,
    };
}
